import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { RefreshCw, X, ZoomIn, ZoomOut } from "lucide-react";
import {
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

import {
  getOpportunityDetails,
  getSalesHistory,
} from "../api";
import {
  calculatePeriodMetrics,
  calculateTradeEstimate,
  type PriceOverride,
} from "../metrics";
import type {
  ChartWindow,
  Opportunity,
  SalesHistoryPoint,
} from "../types";
import { useCurrency } from "../../currency/CurrencyContext";
import { HistoryChart, type HistoryChartRange } from "./HistoryChart";
import { PriceLadder } from "./PriceLadder";

interface DealDetailsProps {
  opportunity: Opportunity;
  profitableSpreadPct: number;
  priceOverride?: PriceOverride;
  onPriceSelect: (tone: "buy" | "sell", price: number) => void;
}

function formatNumber(value: number, digits = 0): string {
  return new Intl.NumberFormat("ru-RU", {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(value);
}

function Metric({
  label,
  value,
  note,
  helpText,
}: {
  label: string;
  value: ReactNode;
  note: string;
  helpText?: string;
}) {
  return (
    <div className="detail-metric">
      <span className="detail-metric-label">
        {label}
        {helpText && (
          <span
            className="metric-help"
            tabIndex={0}
            aria-label={`Справка: ${label}`}
          >
            ?
            <span className="metric-help-tooltip" role="tooltip">
              {helpText}
            </span>
          </span>
        )}
      </span>
      <strong>{value}</strong>
      <small>{note}</small>
    </div>
  );
}

export function DealDetails({
  opportunity,
  profitableSpreadPct,
  priceOverride,
  onPriceSelect,
}: DealDetailsProps) {
  const { formatPrice } = useCurrency();
  const [days, setDays] = useState<ChartWindow>(14);
  const [selectedRange, setSelectedRange] = useState<HistoryChartRange | null>(
    null,
  );
  const [zoomedRange, setZoomedRange] = useState<HistoryChartRange | null>(
    null,
  );
  const [queriesEnabled, setQueriesEnabled] = useState(false);
  const tradeEstimate = calculateTradeEstimate(opportunity, priceOverride);
  useEffect(() => {
    const frame = window.requestAnimationFrame(() => setQueriesEnabled(true));
    return () => window.cancelAnimationFrame(frame);
  }, []);
  const historyQuery = useQuery({
    queryKey: [
      "sales-history",
      opportunity.skin_id,
      opportunity.sell_market,
      days,
    ],
    queryFn: ({ signal }) =>
      getSalesHistory(
        {
          skin_id: opportunity.skin_id,
          market_code: opportunity.sell_market,
          days,
        },
        signal,
      ),
    placeholderData: keepPreviousData,
    staleTime: 60_000,
    enabled: queriesEnabled,
  });
  const detailsQuery = useQuery({
    queryKey: [
      "opportunity-details",
      opportunity.skin_id,
      opportunity.buy_market,
      opportunity.sell_market,
      tradeEstimate.buyPrice,
      tradeEstimate.sellPrice,
      tradeEstimate.profitAbs,
      profitableSpreadPct,
      opportunity.history_metrics.median_window_days,
      opportunity.history_metrics.median_weighted,
    ],
    queryFn: ({ signal }) =>
      getOpportunityDetails(
        {
          skin_id: opportunity.skin_id,
          buy_market: opportunity.buy_market,
          sell_market: opportunity.sell_market,
          buy_price: tradeEstimate.buyPrice,
          sell_price: tradeEstimate.sellPrice,
          profit_abs: tradeEstimate.profitAbs,
          buy_available_count: opportunity.buy_available_count,
          sell_available_count: opportunity.sell_available_count,
          median_window_days: opportunity.history_metrics.median_window_days,
          median_weighted: opportunity.history_metrics.median_weighted,
          profitable_spread_pct: profitableSpreadPct,
        },
        signal,
      ),
    placeholderData: keepPreviousData,
    staleTime: 30_000,
    enabled: queriesEnabled,
  });
  const fallbackPoints: SalesHistoryPoint[] =
    days === 14 ? (opportunity.sell_market_stats.chart_14d ?? []) : [];
  const historyPoints = historyQuery.data?.rows ?? fallbackPoints;
  const selectedHistoryPoints = useMemo(() => {
    if (
      selectedRange === null ||
      selectedRange.startIndex < 0 ||
      selectedRange.endIndex >= historyPoints.length
    ) {
      return historyPoints;
    }
    return historyPoints.slice(
      selectedRange.startIndex,
      selectedRange.endIndex + 1,
    );
  }, [historyPoints, selectedRange]);
  const visibleHistoryPoints = useMemo(() => {
    if (
      zoomedRange === null ||
      zoomedRange.startIndex < 0 ||
      zoomedRange.endIndex >= historyPoints.length
    ) {
      return historyPoints;
    }
    return historyPoints.slice(
      zoomedRange.startIndex,
      zoomedRange.endIndex + 1,
    );
  }, [historyPoints, zoomedRange]);
  const chartSelectedRange = useMemo(() => {
    if (selectedRange === null) {
      return null;
    }

    const visibleOffset = zoomedRange?.startIndex ?? 0;
    const startIndex = selectedRange.startIndex - visibleOffset;
    const endIndex = selectedRange.endIndex - visibleOffset;
    if (
      startIndex < 0 ||
      endIndex >= visibleHistoryPoints.length ||
      (zoomedRange !== null &&
        selectedRange.startIndex === zoomedRange.startIndex &&
        selectedRange.endIndex === zoomedRange.endIndex)
    ) {
      return null;
    }
    return { startIndex, endIndex };
  }, [selectedRange, visibleHistoryPoints.length, zoomedRange]);
  const selectedRangeLabel =
    selectedRange !== null &&
    historyPoints[selectedRange.startIndex] &&
    historyPoints[selectedRange.endIndex]
      ? `${historyPoints[selectedRange.startIndex].label} – ${
          historyPoints[selectedRange.endIndex].label
        }`
      : null;
  const selectionIsZoomed =
    selectedRange !== null &&
    zoomedRange !== null &&
    selectedRange.startIndex === zoomedRange.startIndex &&
    selectedRange.endIndex === zoomedRange.endIndex;
  const chartId = `${opportunity.skin_id}-${days}`;
  const metricsPeriodLabel = selectedRangeLabel ?? `${days} дней`;
  const periodMetrics = useMemo(
    () =>
      calculatePeriodMetrics(
        selectedHistoryPoints,
        Math.max(
          detailsQuery.data?.deal_quality.safe_quantity ??
            opportunity.deal_quality.safe_quantity,
          1,
        ),
      ),
    [
      detailsQuery.data?.deal_quality.safe_quantity,
      selectedHistoryPoints,
      opportunity.deal_quality.safe_quantity,
    ],
  );
  const averageDifference =
    periodMetrics.averagePrice !== null && periodMetrics.averagePrice > 0
      ? ((tradeEstimate.sellPrice - periodMetrics.averagePrice) /
          periodMetrics.averagePrice) *
        100
      : null;
  const nextBuyLevels = detailsQuery.data?.buy_market_stats.levels;
  const nextSellLevels = detailsQuery.data?.sell_orders_stats.levels;

  const buyLevels =
    nextBuyLevels && nextBuyLevels.length > 0
      ? nextBuyLevels
      : (opportunity.buy_market_stats.levels ?? []);
  const sellLevels =
    nextSellLevels && nextSellLevels.length > 0
      ? nextSellLevels
      : (opportunity.sell_orders_stats.levels ?? []);

  return (
    <div className="deal-details">
      <section className="history-panel">
        <header className="detail-section-header">
          <div>
            <span>История продаж</span>
            <strong>{opportunity.sell_market_name}</strong>
          </div>
          <div className="history-actions">
            {historyQuery.isFetching && (
              <RefreshCw className="is-spinning" aria-label="Обновление графика" />
            )}
            <div className="segmented-control">
              {([14, 30, 60] as ChartWindow[]).map((windowDays) => (
                <button
                  className={days === windowDays ? "is-selected" : ""}
                  type="button"
                  key={windowDays}
                  onClick={() => {
                    setSelectedRange(null);
                    setZoomedRange(null);
                    setDays(windowDays);
                  }}
                >
                  {windowDays} дней
                </button>
              ))}
            </div>
            {selectedRangeLabel && (
              <div
                className={`history-range-chip${
                  selectionIsZoomed ? " is-zoomed" : ""
                }`}
              >
                <button
                  className="history-range-zoom"
                  type="button"
                  onClick={() => {
                    if (selectionIsZoomed) {
                      setZoomedRange(null);
                    } else {
                      setZoomedRange(selectedRange);
                    }
                  }}
                  title={
                    selectionIsZoomed
                      ? "Вернуть полный график"
                      : "Приблизить выбранный период"
                  }
                >
                  {selectionIsZoomed ? (
                    <ZoomOut aria-hidden="true" />
                  ) : (
                    <ZoomIn aria-hidden="true" />
                  )}
                  <span>{selectedRangeLabel}</span>
                </button>
                <button
                  className="history-range-clear"
                  type="button"
                  onClick={() => {
                    setSelectedRange(null);
                    setZoomedRange(null);
                  }}
                  title="Сбросить выбранный период"
                  aria-label="Сбросить выбранный период"
                >
                  <X aria-hidden="true" />
                </button>
              </div>
            )}
          </div>
        </header>
        <div className="history-chart-stage">
          <HistoryChart
            id={chartId}
            points={visibleHistoryPoints}
            selectedRange={chartSelectedRange}
            onRangeChange={(range) => {
              if (range === null) {
                setSelectedRange(null);
                return;
              }

              const sourceOffset = zoomedRange?.startIndex ?? 0;
              setSelectedRange({
                startIndex: sourceOffset + range.startIndex,
                endIndex: sourceOffset + range.endIndex,
              });
            }}
          />
        </div>
      </section>

      <section className="detail-metrics" aria-label="Метрики периода">
        <Metric
          label="Средняя цена"
          value={formatPrice(periodMetrics.averagePrice)}
          note={
            averageDifference === null
              ? metricsPeriodLabel
              : `${formatNumber(Math.abs(averageDifference), 2)}% ${
                  averageDifference >= 0 ? "выше" : "ниже"
                } средней`
          }
        />
        <Metric
          label="Медианная цена"
          value={formatPrice(periodMetrics.medianPrice)}
          note={`${metricsPeriodLabel} · с весом продаж`}
          helpText="Это цена примерно посередине всех продаж. Половина предметов была продана дешевле, а половина — дороже. Одна случайно очень дорогая или дешёвая продажа почти не влияет на результат."
        />
        <Metric
          label="Продано"
          value={`${formatNumber(periodMetrics.salesCount)} шт.`}
          note={metricsPeriodLabel}
        />
        <Metric
          label="Волатильность"
          value={
            periodMetrics.volatilityPct === null
              ? "—"
              : `${formatNumber(periodMetrics.volatilityPct, 2)}%`
          }
          note={metricsPeriodLabel}
          helpText="Показывает, насколько сильно цена менялась за выбранный период. Чем ниже значение, тем стабильнее цена предмета."
        />
        <Metric
          label="Активные дни"
          value={
            <>
              {formatNumber(periodMetrics.activeDays)} дн.
              <span className="metric-value-secondary">
                {formatNumber(
                  (periodMetrics.activeDays /
                    Math.max(selectedHistoryPoints.length, 1)) *
                    100,
                  2,
                )}%
              </span>
            </>
          }
          note={`из ${selectedHistoryPoints.length} дней с продажами`}
        />
        <Metric
          label="Средние продажи в день"
          value={`${formatNumber(periodMetrics.averageDailySales, 2)} шт.`}
          note={metricsPeriodLabel}
        />
      </section>

      <section className="ladders-panel" aria-label="Лестницы цен">
        <PriceLadder
          buy={{
            marketName: opportunity.buy_market_name,
            marketIconUrl: opportunity.buy_market_icon_url,
            marketLink: opportunity.buy_link,
            levels: buyLevels,
            selectedPrice: tradeEstimate.buyPrice,
            onPriceSelect: (price) => onPriceSelect("buy", price),
          }}
          sell={{
            marketName: opportunity.sell_market_name,
            marketIconUrl: opportunity.sell_market_icon_url,
            marketLink: opportunity.sell_link,
            levels: sellLevels,
            selectedPrice: tradeEstimate.sellPrice,
            onPriceSelect: (price) => onPriceSelect("sell", price),
          }}
        />
      </section>

    </div>
  );
}
